import type { GameSettings, GameState, GameStats, ResourceId } from './types';
import { BALANCE, RESOURCE_IDS } from './content';
import { collectModifiers } from './modifiers';

export const SAVE_VERSION = 1;

/** Forage the tribe always begins with, so an unassigned start is survivable. */
const OPENING_FORAGE = 30;

function emptyResources(): Record<ResourceId, number> {
  const resources: Record<ResourceId, number> = {};
  for (const resourceId of RESOURCE_IDS) resources[resourceId] = 0;
  return resources;
}

function defaultSettings(): GameSettings {
  return {
    notation: 'standard',
    showRates: true,
    confirmPrestige: true,
    reducedMotion: false,
  };
}

function defaultStats(): GameStats {
  return {
    totalPlaySeconds: 0,
    totalGenerations: 0,
    totalTalesSeen: 0,
    totalManualGathers: 0,
    bestPopulation: 0,
    bestGenerationAk: 0,
    lifetimeProduced: emptyResources(),
  };
}

/** A fresh save with no history at all. */
export function createInitialState(now: number = Date.now()): GameState {
  const state: GameState = {
    version: SAVE_VERSION,
    createdAt: now,
    lastTick: now,

    ageIndex: 0,
    generation: 1,
    generationsInAge: 0,
    rites: 0,
    completedAt: null,

    resources: emptyResources(),
    population: BALANCE.baseStartPopulation,
    peakPopulation: BALANCE.baseStartPopulation,
    jobs: {},
    buildings: {},
    techs: [],

    ancestralKnowledge: 0,
    ancestral: {},

    vigorStacks: 0,
    vigorDecay: 0,
    buffs: [],

    taleTimer: BALANCE.taleIntervalMinSeconds,
    tales: [],

    seed: (now ^ 0x9e3779b9) >>> 0,

    stats: defaultStats(),
    settings: defaultSettings(),
  };

  state.resources.forage = OPENING_FORAGE;
  return state;
}

/**
 * Reset everything that belongs to a single generation, keeping the permanent
 * layer (Ancestral Memory, age, rites, tales, stats, settings) intact.
 * Used by both the generation prestige and the age advance.
 */
export function beginGeneration(state: GameState): GameState {
  // Ancestral effects are the only source of head starts, so measure them
  // against a state stripped of run-scoped progress.
  const bare: GameState = {
    ...state,
    techs: [],
    buildings: {},
    buffs: [],
    vigorStacks: 0,
  };
  const modifiers = collectModifiers(bare);

  const resources = emptyResources();
  resources.forage = OPENING_FORAGE;
  for (const [resourceId, amount] of Object.entries(modifiers.startResources)) {
    resources[resourceId] = (resources[resourceId] ?? 0) + (amount ?? 0);
  }

  const startingPopulation = Math.max(1, Math.floor(modifiers.startPopulation));

  return {
    ...state,
    resources,
    population: startingPopulation,
    peakPopulation: startingPopulation,
    jobs: {},
    buildings: {},
    techs: [...modifiers.startTechs],
    vigorStacks: 0,
    vigorDecay: 0,
    buffs: [],
    taleTimer: BALANCE.taleIntervalMinSeconds,
  };
}

/** Total tribe members currently assigned to a job. */
export function assignedWorkers(state: GameState): number {
  let total = 0;
  for (const count of Object.values(state.jobs)) total += count;
  return total;
}

/** Whole tribe members available to assign. */
export function availableWorkers(state: GameState): number {
  return Math.max(0, Math.floor(state.population) - assignedWorkers(state));
}
